import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ActivityCard, MixedText, PlayButton, ProgressPill } from "../grammar/shared";
import { useLessonAudio } from "../grammar/useLessonAudio";
import { completeAiQuizAttempt, patchAiQuizAttempt, reportAiQuizQuestion, startAiQuizAttempt } from "../../api";
import { duffFeedback } from "../../utils/duff";
import { ITEM_RENDERERS, SKILL_LABELS, TYPE_LABELS } from "./items";
import { GhostButton, PrimaryButton } from "./items/shared";
import QuestionEditor from "./admin/QuestionEditor";

/**
 * AiTopicQuiz — the journey's Quiz step, AI edition (docs/AI_TOPIC_QUIZ.md).
 *
 * Flow: loading (bank generation on a first start) → one question per screen
 * with instant feedback + an explanation card → results. Every answer is
 * reported to the server as it happens (resume on any device); the server
 * owns the score and marks the journey step. A missed word comes back later
 * in the same attempt in a different format (remediation, pulled from the
 * attempt's reserve list) — those items never count toward the score but
 * flip the word to "mastered".
 */
const REMEDIATION_GAP = 3;
const REPORT_REASONS = [
  ["wrong_answer", "The marked answer is wrong"],
  ["ambiguous", "More than one answer fits"],
  ["unclear", "Confusing or unclear"],
  ["off_topic", "Not from this topic"],
];

const kindOf = (q) => (q.type === "mc" ? `mc_${q.data?.variant || ""}` : q.type);

// Items that are ABOUT the dialogue get a "Read the dialogue" toggle; word
// items don't (they test recall of the word itself).
const isDialogueItem = (q) =>
  q.type === "true_false" || (q.type === "mc" && ["who_said", "comprehension", "best_reply"].includes(q.data?.variant));

function DialoguePanel({ lines, audio, open, onToggle }) {
  if (!lines?.length) return null;
  return (
    <div className="mb-4">
      <div className="flex justify-center">
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={open}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-[filter] hover:brightness-110"
          style={{ background: "var(--violet-bg)", color: "var(--tag-violet)", border: "1px solid var(--panel-line)" }}
        >
          <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
          </svg>
          {open ? "Hide the dialogue" : "Read the dialogue"}
        </button>
      </div>
      {open && (
        <div className="mt-3 max-h-72 overflow-y-auto scroll-elegant rounded-xl px-4 py-3 flex flex-col gap-2.5"
             style={{ background: "var(--ink)", border: "1px solid var(--panel-line)" }}>
          {lines.map((t) => (
            <div key={t.n} className="flex items-start gap-3">
              {audio.audioAllowed && <PlayButton text={t.ar} playingText={audio.playingText} onPlay={audio.play} className="mt-1 shrink-0" />}
              <div className="min-w-0 flex-1">
                {t.speaker && <span className="text-[11px] uppercase tracking-wide block" style={{ color: "var(--gold-bright)" }}>{t.speaker}</span>}
                <p dir="rtl" lang="ar" className="font-arabic text-2xl leading-[1.9] m-0 text-right" style={{ color: "var(--parchment)" }}>{t.ar}</p>
                {t.en && <p className="text-xs m-0 mt-0.5" style={{ color: "var(--parchment-dim)" }}>{t.en}</p>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// App.js mounts the topic area twice (a desktop tree and a mobile tree, one
// hidden by CSS). Only the instance matching the live breakpoint may run —
// otherwise every attempt starts twice and audio plays twice.
function useIsDesktop() {
  const query = "(min-width: 768px)";
  const [desktop, setDesktop] = useState(() => (typeof window !== "undefined" ? window.matchMedia(query).matches : true));
  useEffect(() => {
    const mq = window.matchMedia(query);
    const handler = (e) => setDesktop(e.matches);
    mq.addEventListener?.("change", handler);
    return () => mq.removeEventListener?.("change", handler);
  }, []);
  return desktop;
}

export default function AiTopicQuiz({ topicId, topicName, layout, onExit, onComplete, onProgress }) {
  const isDesktop = useIsDesktop();
  const active = layout ? (layout === "desktop") === isDesktop : true;
  const audio = useLessonAudio();
  const [phase, setPhase] = useState("loading"); // loading | error | question | results
  const [error, setError] = useState(null);
  const [slow, setSlow] = useState(false);
  const [attempt, setAttempt] = useState(null);
  const [queue, setQueue] = useState([]); // [{ q, remediation }]
  const [idx, setIdx] = useState(0);
  const [answered, setAnswered] = useState(null); // { correct, answer }
  const [streak, setStreak] = useState(0);
  const [resumedFrom, setResumedFrom] = useState(0);
  const [summary, setSummary] = useState(null);
  const [report, setReport] = useState(null); // null | "open" | "sent"
  const [dialogueOpen, setDialogueOpen] = useState(false);
  const [editing, setEditing] = useState(false); // staff: QuestionEditor open for the current item
  const reserveRef = useRef([]);
  const resultsRef = useRef([]); // everything answered this session (for scoring fallbacks)
  const pendingRef = useRef([]); // results not yet confirmed by the server
  const mainTotalRef = useRef(0);
  const inFlightRef = useRef(null); // topicId of a start request in flight

  const load = useCallback(async (resume) => {
    // StrictMode (dev) runs the mount effect twice; one start per topic is enough.
    if (inFlightRef.current === topicId) return;
    inFlightRef.current = topicId;
    setPhase("loading");
    setError(null);
    setSlow(false);
    setSummary(null);
    setAnswered(null);
    setStreak(0);
    setReport(null);
    const slowTimer = setTimeout(() => setSlow(true), 4000);
    try {
      const data = await startAiQuizAttempt(topicId, { resume });
      const done = new Set((data.results || []).filter((r) => !r.remediation).map((r) => r.question_id));
      const remaining = (data.questions || []).filter((q) => !done.has(q.id));
      mainTotalRef.current = (data.questions || []).length;
      resultsRef.current = data.results || [];
      pendingRef.current = [];
      reserveRef.current = (data.reserve || []).filter((q) => !done.has(q.id));
      setAttempt(data);
      setResumedFrom(done.size);
      setQueue(remaining.map((q) => ({ q, remediation: false })));
      setIdx(0);
      if (remaining.length === 0) {
        // everything was answered before the connection dropped — just finish
        const out = await completeAiQuizAttempt(data.id, { results: [] });
        setSummary(out);
        setPhase("results");
        if (onComplete) onComplete();
      } else {
        setPhase("question");
      }
    } catch (e) {
      setError(e);
      setPhase("error");
    } finally {
      clearTimeout(slowTimer);
      inFlightRef.current = null;
    }
  }, [topicId, onComplete]);

  useEffect(() => {
    if (!active) return undefined;
    load(true);
    return () => audio.stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topicId, active]);

  const current = queue[idx] || null;
  const mainAnswered = resultsRef.current.filter((r) => !r.remediation).length;
  const progress = mainTotalRef.current ? Math.min(100, Math.round((mainAnswered / mainTotalRef.current) * 100)) : 0;

  // When the reserve has nothing for a missed word, build a plain meaning
  // question from the attempt's word list (the server recognises the
  // "syn_<wordId>_" id prefix and credits the word on a correct answer).
  const syntheticFor = useCallback((q) => {
    const words = attempt?.targets || [];
    const wid = q.targets.find((t) => words.some((w) => w.id === t));
    const word = words.find((w) => w.id === wid);
    if (!word || !word.en) return null;
    const others = words.filter((w) => w.id !== wid && w.en && w.ar);
    if (others.length < 2) return null;
    const pool = [...others].sort(() => Math.random() - 0.5).slice(0, 3);
    const toArabic = !(q.type === "mc" && q.data?.variant === "en_ar");
    return {
      id: `syn_${wid}_${Date.now().toString(36)}`,
      type: "mc",
      skill: "vocab",
      difficulty: 1,
      targets: [wid],
      prompt_en: toArabic ? `Which word means "${word.en}"?` : "What does this word mean?",
      explanation: `${word.ar} — ${word.en}`,
      audio_text: word.ar,
      bank_id: null,
      data: toArabic
        ? { variant: "en_ar", prompt_ar: "", choices: [word.ar, ...pool.map((w) => w.ar)], correct_index: 0 }
        : { variant: "ar_en", prompt_ar: word.ar, choices: [word.en, ...pool.map((w) => w.en)], correct_index: 0 },
    };
  }, [attempt]);

  const scheduleRemediation = useCallback((q) => {
    if (!q.targets?.length) return;
    const usedIds = new Set(queue.map((e) => e.q.id));
    const kind = kindOf(q);
    let pick = reserveRef.current.find(
      (r) => !usedIds.has(r.id) && kindOf(r) !== kind && r.targets.some((t) => q.targets.includes(t))
    ) || reserveRef.current.find((r) => !usedIds.has(r.id) && r.targets.some((t) => q.targets.includes(t)));
    if (pick) reserveRef.current = reserveRef.current.filter((r) => r.id !== pick.id);
    else pick = syntheticFor(q);
    if (!pick) return;
    setQueue((prev) => {
      const at = Math.min(prev.length, idx + 1 + REMEDIATION_GAP);
      const next = [...prev];
      next.splice(at, 0, { q: pick, remediation: true });
      return next;
    });
  }, [queue, idx, syntheticFor]);

  const handleAnswer = useCallback(({ correct, answer }) => {
    if (!current || answered) return;
    const entry = { question_id: current.q.id, correct: !!correct, answer, remediation: current.remediation };
    resultsRef.current = [...resultsRef.current, entry];
    pendingRef.current = [...pendingRef.current, entry];
    setAnswered({ correct: !!correct, answer });
    setStreak((s) => (correct ? s + 1 : 0));
    try { duffFeedback(correct ? "correct" : "wrong"); } catch { /* feedback is a bonus */ }
    if (!correct && !current.remediation) scheduleRemediation(current.q);
    // explanation audio: the Arabic the question was about
    const t = current.q.type;
    if (current.q.audio_text && audio.audioAllowed && t !== "audio_mc" && t !== "order_sentence") {
      audio.play(current.q.audio_text);
    }
    if (attempt) {
      const batch = pendingRef.current;
      patchAiQuizAttempt(attempt.id, { results: batch, currentIndex: idx + 1 })
        .then(() => { pendingRef.current = pendingRef.current.filter((r) => !batch.includes(r)); })
        .catch(() => { /* kept in pendingRef; rides along with the completion call */ });
    }
  }, [current, answered, attempt, idx, audio, scheduleRemediation]);

  const finish = useCallback(async () => {
    if (!attempt) return;
    setPhase("loading");
    try {
      const out = await completeAiQuizAttempt(attempt.id, { results: pendingRef.current });
      pendingRef.current = [];
      setSummary(out);
      setPhase("results");
      if (onComplete) onComplete();
      if (onProgress) onProgress();
    } catch (e) {
      setError(e);
      setPhase("error");
    }
  }, [attempt, onComplete, onProgress]);

  const next = () => {
    setAnswered(null);
    setReport(null);
    setDialogueOpen(false);
    audio.stop();
    if (idx + 1 >= queue.length) finish();
    else setIdx((i) => i + 1);
  };

  const sendReport = async (reason) => {
    if (!current || !attempt) return;
    setReport("sent");
    try {
      await reportAiQuizQuestion({ bankId: current.q.bank_id, questionId: current.q.id, reason });
    } catch { /* best effort */ }
  };

  // Staff hand-corrections (docs/AI_TOPIC_QUIZ.md): swap the fixed question
  // into the queue and reserve so the screen shows the correction at once.
  const applyEdit = (saved) => {
    setQueue((prev) => prev.map((e) => (e.q.id === saved.id ? { ...e, q: { ...e.q, ...saved } } : e)));
    reserveRef.current = reserveRef.current.map((r) => (r.id === saved.id ? { ...r, ...saved } : r));
    setEditing(false);
  };
  const applyDelete = (removed) => {
    reserveRef.current = reserveRef.current.filter((r) => r.id !== removed.id);
    setEditing(false);
    setAnswered(null);
    setQueue((prev) => {
      const next = prev.filter((e) => e.q.id !== removed.id);
      if (next.length === 0) finish();
      else if (idx >= next.length) setIdx(next.length - 1);
      return next;
    });
  };

  const remainingMain = useMemo(() => queue.slice(idx).filter((e) => !e.remediation).length, [queue, idx]);

  if (!active) return null;

  // ── Frame ────────────────────────────────────────────────────────────────
  const header = (
    <div className="flex items-center justify-between gap-3 mb-4">
      <div className="min-w-0">
        <p className="text-xs uppercase tracking-wide m-0" style={{ color: "var(--parchment-dim)" }}>Quiz</p>
        <p className="font-cormorant font-semibold text-xl m-0 truncate" style={{ color: "var(--parchment)" }}>{topicName || "Topic"}</p>
      </div>
      <GhostButton onClick={onExit}>{phase === "results" ? "Back to topic" : "Exit"}</GhostButton>
    </div>
  );

  if (phase === "loading") {
    return (
      <div className="dark-scope max-w-3xl mx-auto">
        <ActivityCard>
          {header}
          <div className="text-center py-10">
            <div className="mx-auto w-10 h-10 rounded-full border-2 border-t-transparent animate-spin mb-4" style={{ borderColor: "var(--gold)", borderTopColor: "transparent" }} />
            <p className="text-base m-0" style={{ color: "var(--parchment)" }}>{summary ? "Scoring…" : "Preparing your quiz…"}</p>
            {slow && (
              <p className="text-sm mt-2 max-w-md mx-auto" style={{ color: "var(--parchment-dim)" }}>
                Writing fresh questions for this topic — the first time takes about half a minute. Next time it's instant.
              </p>
            )}
          </div>
        </ActivityCard>
      </div>
    );
  }

  if (phase === "error") {
    const credit = error?.status === 402;
    const resets = error?.resetsAt ? new Date(error.resetsAt) : null;
    return (
      <div className="dark-scope max-w-3xl mx-auto">
        <ActivityCard>
          {header}
          <div className="text-center py-8">
            <p className="text-4xl m-0" aria-hidden="true">{credit ? "⏳" : "⚠️"}</p>
            <p className="text-base mt-3 mb-1" style={{ color: "var(--parchment)" }}>
              {credit ? "You've used this month's AI allowance." : "The quiz couldn't start."}
            </p>
            <p className="text-sm mb-5 max-w-md mx-auto" style={{ color: "var(--parchment-dim)" }}>
              {credit
                ? `It resets ${resets && !isNaN(resets) ? "on " + resets.toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "next month"}. Topics that already have questions still work.`
                : error?.message || "Please try again in a moment."}
            </p>
            <div className="flex justify-center gap-2">
              {!credit && <PrimaryButton onClick={() => load(true)}>Try again</PrimaryButton>}
              <GhostButton onClick={onExit}>Back to topic</GhostButton>
            </div>
          </div>
        </ActivityCard>
      </div>
    );
  }

  if (phase === "results" && summary) {
    const passed = summary.passed;
    const skills = Object.entries(summary.per_skill || {});
    return (
      <div className="dark-scope max-w-3xl mx-auto">
        <ActivityCard>
          {header}
          <div className="text-center py-4">
            <p className="text-4xl m-0" aria-hidden="true">{passed ? "🏆" : "📚"}</p>
            <p className="font-cormorant font-semibold text-5xl mt-2 mb-0" style={{ color: passed ? "var(--teal)" : "var(--parchment)" }}>
              {summary.score_percent}%
            </p>
            <p className="text-sm mt-1 mb-3" style={{ color: "var(--parchment-dim)" }}>
              pass mark {summary.pass_percent}% · best {summary.best_score}%
            </p>
            <p className="text-base mb-2" style={{ color: "var(--parchment)" }}>
              {passed
                ? summary.mastered
                  ? "Topic complete — and every word answered right at least once. Mastered."
                  : "Topic complete! A few words slipped once; they're listed below."
                : "Not yet — review the words below and take it again with fresh questions."}
            </p>
            {summary.mastered && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold" style={{ background: "var(--gold-bg)", color: "var(--gold-bright)", border: "1px solid var(--gold-dim)" }}>
                ★ Mastered
              </span>
            )}
          </div>

          {skills.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 max-w-xl mx-auto mt-4">
              {skills.map(([skill, s]) => {
                const pct = s.total ? Math.round((s.correct / s.total) * 100) : 0;
                return (
                  <div key={skill} className="px-3 py-2 rounded-xl" style={{ background: "var(--ink)", border: "1px solid var(--panel-line)" }}>
                    <div className="flex justify-between text-xs mb-1" style={{ color: "var(--parchment-dim)" }}>
                      <span>{SKILL_LABELS[skill] || skill}</span><span>{s.correct}/{s.total}</span>
                    </div>
                    <div className="h-1.5 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.08)" }}>
                      <div className="h-full rounded-full" style={{ width: `${pct}%`, background: "linear-gradient(90deg, var(--gold-dim), var(--gold))" }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {summary.missed_words?.length > 0 && (
            <div className="max-w-xl mx-auto mt-5">
              <p className="text-xs uppercase tracking-wide mb-2" style={{ color: "var(--parchment-dim)" }}>Words to revisit</p>
              <div className="flex flex-wrap gap-2">
                {summary.missed_words.map((w) => (
                  <span key={w.id} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl" style={{ background: "var(--ink)", border: "1px solid var(--panel-line)" }}>
                    <span dir="rtl" lang="ar" className="font-arabic text-xl" style={{ color: "var(--parchment)" }}>{w.ar}</span>
                    <span className="text-xs" style={{ color: "var(--parchment-dim)" }}>{w.en}</span>
                    {audio.audioAllowed && <PlayButton text={w.ar} playingText={audio.playingText} onPlay={audio.play} />}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="flex flex-col sm:flex-row justify-center gap-2 mt-6">
            <PrimaryButton onClick={() => load(false)}>Retake with new questions</PrimaryButton>
            <GhostButton onClick={onExit}>Back to topic</GhostButton>
          </div>
        </ActivityCard>
      </div>
    );
  }

  if (!current) return null;
  const Renderer = ITEM_RENDERERS[current.q.type];
  return (
    <div className="dark-scope max-w-3xl mx-auto">
      <ActivityCard>
        {header}
        <div className="flex items-center justify-between gap-3 mb-2">
          <ProgressPill>
            {TYPE_LABELS[current.q.type] || current.q.type}
            {current.remediation && " · second look"}
          </ProgressPill>
          <div className="flex items-center gap-3 text-sm" style={{ color: "var(--parchment-dim)" }}>
            {streak >= 3 && <span className="font-semibold" style={{ color: "var(--gold-bright)" }}>🔥 {streak} in a row</span>}
            <span>{remainingMain} left</span>
            {attempt?.can_edit && current.q.bank_id && (
              <button type="button" onClick={() => setEditing(true)} className="text-xs underline" style={{ color: "var(--tag-violet)" }} title="Staff: correct this question">
                Edit question
              </button>
            )}
          </div>
        </div>
        <div className="h-1.5 rounded-full overflow-hidden mb-5" style={{ background: "rgba(255,255,255,0.08)" }}>
          <div className="h-full rounded-full transition-all" style={{ width: `${progress}%`, background: "linear-gradient(90deg, var(--gold-dim), var(--gold))" }} />
        </div>
        {resumedFrom > 0 && idx === 0 && !answered && (
          <p className="text-xs text-center -mt-2 mb-3" style={{ color: "var(--parchment-dim)" }}>Resumed — {resumedFrom} already answered.</p>
        )}

        {isDialogueItem(current.q) && !answered && (
          <DialoguePanel lines={attempt?.dialogue} audio={audio} open={dialogueOpen} onToggle={() => setDialogueOpen((o) => !o)} />
        )}

        {Renderer ? (
          <Renderer key={`${current.q.id}-${current.remediation ? "r" : "m"}-${current.q.edited_at || ""}`} question={current.q} onAnswer={handleAnswer} audio={audio} />
        ) : (
          <p className="text-sm text-center" style={{ color: "var(--parchment-dim)" }}>
            This question type isn't supported yet.
            <button type="button" className="underline ml-2" onClick={() => handleAnswer({ correct: true, answer: null })}>Skip</button>
          </p>
        )}

        {answered && (
          <div className="mt-6 px-4 py-3 rounded-xl" style={{
            background: answered.correct ? "rgba(52,168,133,0.12)" : "rgba(220,90,90,0.10)",
            border: `1px solid ${answered.correct ? "var(--teal)" : "#dc5a5a"}`,
          }}>
            <div className="flex items-start gap-3">
              <span className="text-xl leading-none mt-0.5" aria-hidden="true">{answered.correct ? "✅" : "💡"}</span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold m-0" style={{ color: answered.correct ? "var(--teal)" : "#e58c8c" }}>
                  {answered.correct ? (current.remediation ? "Got it this time" : "Correct") : (current.remediation ? "Still tricky — it's listed at the end" : "Not quite")}
                </p>
                {current.q.explanation && (
                  <p className="text-sm mt-1 mb-0 leading-relaxed" style={{ color: "var(--parchment)" }}>
                    <MixedText text={current.q.explanation} />
                  </p>
                )}
                {!answered.correct && !current.remediation && current.q.targets?.length > 0 && (
                  <p className="text-xs mt-1.5 mb-0" style={{ color: "var(--parchment-dim)" }}>This word will come back in a different form.</p>
                )}
              </div>
              {current.q.audio_text && audio.audioAllowed && (
                <PlayButton text={current.q.audio_text} playingText={audio.playingText} onPlay={audio.play} />
              )}
            </div>
            <div className="flex items-center justify-between gap-3 mt-3">
              {report === "sent" ? (
                <span className="text-xs" style={{ color: "var(--parchment-dim)" }}>Thanks — we'll review this question.</span>
              ) : report === "open" ? (
                <select
                  defaultValue=""
                  onChange={(e) => e.target.value && sendReport(e.target.value)}
                  className="text-xs rounded-lg px-2 py-1"
                  style={{ background: "var(--ink)", color: "var(--parchment)", border: "1px solid var(--panel-line)" }}
                >
                  <option value="" disabled>What's wrong with it?</option>
                  {REPORT_REASONS.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
                </select>
              ) : current.q.bank_id ? (
                <button type="button" onClick={() => setReport("open")} className="text-xs underline" style={{ color: "var(--parchment-dim)" }}>
                  Report this question
                </button>
              ) : <span />}
              <PrimaryButton onClick={next}>{idx + 1 >= queue.length ? "See results" : "Next"}</PrimaryButton>
            </div>
          </div>
        )}
      </ActivityCard>
      {editing && (
        <QuestionEditor question={current.q} onClose={() => setEditing(false)} onSaved={applyEdit} onDeleted={applyDelete} />
      )}
    </div>
  );
}
